import type { Meta } from '@/api/types'
import type { CourseLanguage } from '@/queries/course-list/types'
import type {
  ReviewDraft,
  ReviewErrors,
  TeacherCourse,
  TeacherRatings,
  TeacherProfile,
  TeacherVideo
} from './types'

export const teacherLink = (id: unknown) => {
  const value =
    typeof id === 'string'
      ? id
      : typeof id === 'number' && Number.isSafeInteger(id)
        ? String(id)
        : ''
  return /^\d+$/.test(value) && /[1-9]/.test(value)
    ? { name: 'teacher-profile', params: { teacherId: value } }
    : null
}
export const numeric = (value: unknown, fallback = 0) => {
  const n =
    typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(n) ? n : fallback
}
export const persianNumber = (value: unknown) =>
  new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 }).format(numeric(value))
export const ratingBuckets = (data: TeacherRatings['data'] = []) =>
  [5, 4, 3, 2, 1].map((score) => ({
    score,
    percent: Math.min(
      100,
      Math.max(
        0,
        numeric(data.find((item) => numeric(item.score) === score)?.average) * 100
      )
    )
  }))

export function nextReviewPage(meta?: Partial<Meta>): number | undefined {
  if (!meta) return undefined
  const current = numeric(meta.current_page)
  const last = numeric(meta.last_page)
  if (current < 1 || (last > 0 && current >= last) || meta.next_page === null)
    return undefined
  const next = meta.next_page === undefined ? current + 1 : numeric(meta.next_page)
  return Number.isInteger(next) && next > current && (!last || next <= last)
    ? next
    : undefined
}
export const reviewSorts = [
  { label: 'جدیدترین', orderBy: 2, orderType: 2 },
  { label: 'مفیدترین', orderBy: 3, orderType: 2 },
  { label: 'بیشترین امتیاز', orderBy: 1, orderType: 2 },
  { label: 'کمترین امتیاز', orderBy: 1, orderType: 1 }
] as const
export function validateReview(draft: ReviewDraft): ReviewErrors {
  const errors: ReviewErrors = {}
  if (!Number.isInteger(draft.score) || draft.score < 1 || draft.score > 5)
    errors.score = 'امتیاز ۱ تا ۵ را انتخاب کنید.'
  if (!draft.title.trim()) errors.title = 'عنوان دیدگاه را وارد کنید.'
  if (!draft.comment.trim()) errors.comment = 'متن دیدگاه را وارد کنید.'
  else if (draft.comment.length > 300)
    errors.comment = 'متن دیدگاه حداکثر ۳۰۰ کاراکتر است.'
  return errors
}
export function teacherCourseListCard(
  course: TeacherCourse,
  teacher: TeacherProfile['teacher']
): CourseLanguage {
  const language = (
    value: TeacherCourse['language']
  ): CourseLanguage['language'] => ({
    id: value?.id || '',
    name: value?.name || '',
    faName: value?.faName || value?.name || '',
    image: value?.image || '',
    rtl: value?.rtl ?? false,
    show: value?.show ?? false
  })
  return {
    id: course.id,
    title: course.title,
    poster: course.poster || course.fullPoster || '',
    fullPoster: course.fullPoster || course.poster || '',
    shortDescription: course.shortDescription || '',
    price: String(course.price ?? 0),
    oldPrice: String(course.oldPrice ?? 0),
    score: numeric(course.score),
    teacher: {
      id: teacher.id,
      name: teacher.faName || teacher.enName || 'مدرس بدون نام',
      avatar: teacher.avatar || '',
      title: '',
      score: numeric(teacher.score)
    },
    language: language(course.language),
    inLanguage: language(course.inLanguage),
    level: course.level || { id: '', title: '', priority: 0 },
    userCount: numeric(course.userCount),
    sessionCount: numeric(course.sessionCount),
    duration: numeric(course.duration),
    contentLanguage: course.inLanguage?.faName || course.inLanguage?.name || '',
    isActive: course.isActive,
    isVip: course.isVip,
    bought: course.bought,
    enDateOfRelease: course.enDateOfRelease || undefined,
    dateOfRelease: course.dateOfRelease || undefined,
    users: (course.users || []).map((user) => ({
      id: user.id,
      name: user.name || '',
      avatar: user.avatar || '',
      plan: { ...user.plan, icon: user.plan?.icon || '' }
    }))
  }
}
export const reviewDate = (date: string | null | undefined) => {
  if (!date || Number.isNaN(new Date(date).getTime())) return ''
  return new Intl.DateTimeFormat('fa-IR', { dateStyle: 'medium' }).format(
    new Date(date)
  )
}
export const safeImage = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.trim()) return undefined
  return /^(https?:\/\/|\/(?!\/))/.test(value) ? value : undefined
}
const object = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {}
export function teacherVideoSources(video: TeacherVideo | null | undefined) {
  const providers = object(video?.streamProviders)
  const sizes = {
    p240: [240, 426, 600],
    p360: [360, 640, 800],
    p480: [480, 854, 1000],
    p720: [720, 1280, 2000],
    p1080: [1080, 1920, 4500]
  }
  for (const provider of ['cache', 'iran']) {
    const sources = Object.entries(object(object(providers[provider]).urls))
      .flatMap(([quality, value]) => {
        const size = sizes[quality as keyof typeof sizes]
        const src = safeImage(value)
        if (!size || !src) return []
        const [height, width, bitrate] = size
        return [
          {
            src,
            height,
            width,
            bitrate,
            codec: 'h264',
            label: `${height}p`,
            id: `${height}p`
          }
        ]
      })
      .sort((a, b) => b.height - a.height)
    if (sources.length) return sources
  }
  return []
}
